import React from 'react';
import { Location } from 'history';

import { Box, Button, Flex, Text } from '@rover/kibble/core';
import { useCurrentUser } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';
import getUserAgentFlags from '@rover/shared/js/utils/getUserAgentFlags';
import { storeCrossPageAnalyticsEvent } from '@rover/utilities/fireCrossPageEvent';
import getQueryParams from '@rover/utilities/getQueryParams';

import { TrainerProfileContactButtonClicked } from '../../../events/analyticsEvents';
import {
  encodeSkillAndBehaviorQueryParams,
  SkillsAndBehaviorSlugsDictionary,
} from '../../../utils/skillAndBehaviorQueryParams';

type Props = {
  shortName: string;
  showConsultationText?: boolean;
  location: Location;
  skillsAndBehaviorSlugs?: SkillsAndBehaviorSlugsDictionary;
};

const ContactButton = ({
  shortName,
  showConsultationText,
  skillsAndBehaviorSlugs,
  location,
}: Props): JSX.Element => {
  const { isEmbedded } = getUserAgentFlags();
  const { isAuthenticated } = useCurrentUser();
  const queryParams = new URLSearchParams();
  const existingQueryParams = getQueryParams();

  const urlAddition = 'contact/';

  // Preserve any existing query parameters
  for (const [key, value] of Object.entries(existingQueryParams)) {
    if (Array.isArray(value)) {
      value.forEach((v) => queryParams.append(key, v));
    } else if (value) {
      queryParams.set(key, value);
    }
  }

  if (skillsAndBehaviorSlugs) {
    const encoded = encodeSkillAndBehaviorQueryParams(skillsAndBehaviorSlugs);
    if (encoded.behaviors) {
      queryParams.set('behaviors', encoded.behaviors);
    }
    if (encoded.skills) {
      queryParams.set('skills', encoded.skills);
    }
  }

  const unadornedQueryString = queryParams.toString();

  const queryString = unadornedQueryString ? `?${unadornedQueryString}` : '';

  const targetFullUrlEncoded = encodeURIComponent(
    `${location.pathname}${urlAddition}${queryString}`
  );

  const targetUrl =
    isEmbedded && !isAuthenticated
      ? `rover://web/?url=${targetFullUrlEncoded}&force-auth=true`
      : `${urlAddition}${queryString}`;

  return (
    <Flex
      flexDirection={['column', 'column', 'row']}
      alignItems="center"
      justifyContent="center"
      width="100%"
      paddingX={['0x', '0x', '4x']}
      paddingBottom="0x"
      gap="2x"
    >
      <Box flexGrow="1" display={['none', 'none', 'block']}>
        <Button
          width="100%"
          variant="primary"
          data-testid="contact-button"
          href={targetUrl}
          onClick={() => {
            storeCrossPageAnalyticsEvent(new TrainerProfileContactButtonClicked());
          }}
        >
          {/* TODO (DEV-104195): Translate below text after training pilot launch */}
          Contact {shortName}
        </Button>
      </Box>
      {showConsultationText && (
        /* TODO (DEV-104195): Translate below text before international launch */
        <Box display={['block', 'block', 'none']} marginBottom="2x">
          <Text size="100" textAlign="center" fontWeight="semibold" textColor="tertiary">
            {shortName} offers a consultation before booking.
          </Text>
        </Box>
      )}
      <Button
        display={['block', 'block', 'none']}
        width="100%"
        variant="primary"
        data-testid="contact-button"
        href={targetUrl}
        onClick={() => {
          storeCrossPageAnalyticsEvent(new TrainerProfileContactButtonClicked());
        }}
      >
        {/* TODO (DEV-104195): Translate below text after training pilot launch */}
        Contact {shortName}
      </Button>
    </Flex>
  );
};

export default ContactButton;
